/**
 * ProfileVariant — sidebar content for 1:1 chat rooms (single participant).
 *
 * Renders the room title (inline-editable by its owner) above the twin's hero
 * image, name and tagline, then the room's Topics and Materials (Step 11c, when
 * present), then any available bio, the audience banner, and conversation
 * starters derived from the participant's profile data.
 *
 * Collapsed state is owned by ChatSidebar; this variant always renders
 * its full expanded content.
 */

import { ReactNode, useEffect } from 'react'
import useSWR from 'swr'
import { MapPin } from 'lucide-react'

import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { SidebarDivider } from '@/components/chat/SidebarPrimitives'
import { TwinBio } from '@/components/shared/TwinBio'
import { twinsApi } from '@/lib/api'
import { onFeaturedTwinsEnriched } from '@/lib/services/websocket'
import {
  buildBlurbLine,
  buildLocationLine,
  getProfileCardStarters,
  inferAudienceType,
} from '@/lib/utils/twin-blurb'

import { ChatRoomParticipantDto } from '@digital-twins/shared-types'

import { RoomTitleEditor } from './RoomTitleEditor/RoomTitleEditor'
import { AudienceBanner } from './AudienceBanner/AudienceBanner'

const FALLBACK_IMAGE = '/images/twin-fallback.png'

interface ProfileVariantProps {
  participant: ChatRoomParticipantDto
  populationId: string | null | undefined
  /** The room's audience name, the banner's fallback. */
  populationName?: string | null
  /** The room title; empty falls back to the new-conversation title. */
  roomTitle?: string | null
  /** The viewer owns the room: only then is the title editable. */
  viewerIsOwner?: boolean
  onRename?: (title: string) => unknown
  /** The viewer can read the room's audience; false skips the banner fetch. */
  canFetchAudience?: boolean
  collapsed?: boolean
  onSend?: (text: string) => void
  /** Disables the "Ask about" starters (e.g. while a guest ask-about turn is pending). */
  startersDisabled?: boolean
  /** Topics and Materials sections, right after the profile (built by ChatSidebar). */
  roomSections?: ReactNode
}

export function ProfileVariant(props: ProfileVariantProps) {
  const {
    participant,
    onSend,
    populationId,
    populationName,
    roomTitle,
    viewerIsOwner = false,
    onRename,
    canFetchAudience = false,
    startersDisabled = false,
    roomSections = null,
  } = props

  const swrKey =
    populationId && participant.featuredTwinId
      ? `twin-${populationId}-${participant.personaId}`
      : null
  const {
    data: twin,
    isLoading: twinLoading,
    isValidating,
    error: twinError,
    mutate: mutateTwin,
  } = useSWR(
    swrKey,
    () => twinsApi.getFeaturedTwin(populationId!, participant.personaId),
    // `errorRetryCount` caps SWR's default unlimited retry loop: a persona with no
    // featured-twin record 404s every time, and this profile can stay open for a
    // long while. One retry still covers a transient network blip.
    { revalidateOnFocus: false, dedupingInterval: 0, errorRetryCount: 1 },
  )

  // When enrichment completes (status transitions to 'ready'), the backend has
  // written the profile card and summary — revalidate to show bio + starters.
  useEffect(() => {
    if (participant.enrichmentStatus === 'ready' && swrKey) {
      void mutateTwin()
    }
  }, [participant.enrichmentStatus, swrKey, mutateTwin])

  // Phase 2 of the "Add to Featured" pinning flow emits featured-twins:enriched
  // on the default WebSocket namespace. Re-fetch twin data when it fires so the
  // bio and starters appear without a page refresh.
  useEffect(() => {
    const off = onFeaturedTwinsEnriched(() => {
      if (swrKey) void mutateTwin()
    })
    // `off` resolves to `ws.off(...)`, which socket.io-client types as
    // returning the socket (for chaining) rather than `void`; wrap it so the
    // effect's own cleanup return type stays `() => void`.
    return () => {
      off()
    }
  }, [swrKey, mutateTwin])

  const demographics = (twin?.demographics ?? {}) as Record<string, string>
  const audienceType = inferAudienceType(demographics)
  const locationLine = buildLocationLine(demographics)
  // The authored one-liner from the twin's profile card always wins over any
  // derived line. `participant.tagline` stays ahead of the demographic blurb so
  // the chat path (persona tagline) is unchanged; Quick Study passes the
  // demographic line there, which is exactly the intended fallback.
  const authoredTagline = twin?.profileCard?.tagline?.trim() || null
  const tagline =
    authoredTagline ?? participant.tagline ?? buildBlurbLine(demographics, audienceType) ?? null

  const participantHighlights = participant.highlights?.length ? participant.highlights : undefined
  const profileCard = participantHighlights
    ? { bio: participant.bio, highlights: participantHighlights }
    : (twin?.profileCard ?? null)

  const twinStarters = getProfileCardStarters(twin ?? null)
  const starters = twinStarters.length > 0 ? twinStarters : (participant.conversationStarters ?? [])
  const hasFreshProfile = Boolean(profileCard?.highlights?.length)
  // Render gate for the bio block: an authored card with EITHER bullet highlights
  // or an intro paragraph. Kept separate from `hasFreshProfile` so the loading
  // semantics below stay exactly as they were. There is no prose fallback — a
  // persona with no authored card renders no bio at all.
  const hasAuthoredBio = Boolean(profileCard?.highlights?.length || profileCard?.bio?.trim())
  // A failed fetch (persona has no featured-twin record) must settle, not spin.
  const isProfilePending =
    !hasFreshProfile &&
    !twinError &&
    (twinLoading || isValidating || participant.enrichmentStatus === 'enriching')

  const audienceSection =
    populationName || (canFetchAudience && populationId) ? (
      <>
        <SidebarDivider />
        <section data-testid="chat-sidebar-audience">
          <AudienceBanner
            populationId={populationId}
            populationName={populationName}
            canFetchAudience={canFetchAudience}
          />
        </section>
      </>
    ) : null

  return (
    <div className="flex flex-col gap-4">
      <RoomTitleEditor
        title={roomTitle}
        isEditable={viewerIsOwner}
        onRename={onRename}
        dataTestId="chat-sidebar-room-title"
        className="text-base leading-[1.4]"
      />

      {/* Hero image */}
      <div
        data-testid="chat-sidebar-hero"
        className="h-[220px] w-full shrink-0 overflow-hidden rounded-xl bg-twin-chat-bubble/40"
      >
        {participant.imageUrl ? (
          <img
            src={participant.imageUrl}
            alt={participant.displayName}
            className="h-full w-full object-cover object-top"
            onError={(e) => {
              ;(e.target as HTMLImageElement).src = FALLBACK_IMAGE
            }}
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-twin-chat-bubble/60">
            <span className="text-5xl font-bold text-twin-chat-fg/40">
              {participant.displayName[0]}
            </span>
            {twinLoading && (
              <div className="absolute inset-0 animate-pulse bg-twin-chat-bubble/30" />
            )}
          </div>
        )}
      </div>

      {/* Name + tagline */}
      <div className="flex flex-col gap-1">
        <Heading
          level={2}
          data-testid="chat-sidebar-participant-name"
          className="text-2xl leading-[1.4] text-twin-chat-fg"
        >
          {participant.displayName}
        </Heading>
        {tagline && (
          <div className="flex flex-wrap items-center gap-1.5 leading-[1.4]">
            <Text size="sm" className="text-twin-chat-fg">
              {tagline}
              {participant.audienceTagNames && participant.audienceTagNames.length > 0 && ' · '}
            </Text>
            {participant.audienceTagNames &&
              participant.audienceTagNames.length > 0 &&
              participant.audienceTagNames.map((tagName) => (
                <span
                  key={tagName}
                  className="inline-flex items-center rounded-full bg-twin-chat-bubble/40 px-2 py-0.5 text-[11px] leading-tight text-twin-chat-muted"
                >
                  {tagName}
                </span>
              ))}
          </div>
        )}
        {locationLine && (
          <div className="flex items-center gap-1 text-twin-chat-muted">
            <MapPin className="h-4 w-4" />
            <Text size="sm" className="text-twin-chat-muted">
              {locationLine}
            </Text>
          </div>
        )}
      </div>

      {roomSections}

      {isProfilePending ? (
        <div className="flex flex-col gap-4">
          {/* Bio skeleton */}
          <SidebarDivider />
          <div className="flex flex-col gap-2 animate-pulse">
            <div className="h-3 w-3/4 rounded bg-twin-chat-bubble/40" />
            <div className="h-3 w-full rounded bg-twin-chat-bubble/40" />
            <div className="h-3 w-5/6 rounded bg-twin-chat-bubble/40" />
          </div>
          {audienceSection}
          {/* Ask about skeleton */}
          <SidebarDivider />
          <div className="flex flex-col gap-2 animate-pulse">
            <div className="h-3 w-20 rounded bg-twin-chat-bubble/40" />
            <div className="flex flex-col gap-1.5">
              <div className="h-9 w-full rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-twin-chat-bubble/40" />
              <div className="h-9 w-5/6 rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-twin-chat-bubble/40" />
            </div>
          </div>
        </div>
      ) : (
        <>
          {hasAuthoredBio && (
            <>
              <SidebarDivider />
              <TwinBio
                profileCard={profileCard}
                introClassName="text-twin-chat-fg"
                highlightClassName="text-twin-chat-muted"
              />
            </>
          )}

          {audienceSection}

          {/* Starters are send buttons — without `onSend` (the Quick Study Twins tab,
              which has no thread to send into) they would be dead controls. */}
          {onSend && starters.length > 0 && (
            <>
              <SidebarDivider />
              <div className="flex flex-col gap-1.5" data-testid="chat-sidebar-ask-about">
                <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
                  Ask about
                </Text>
                <div className="flex flex-col gap-1.5">
                  {starters.map((starter) => (
                    <button
                      key={starter}
                      type="button"
                      data-testid="chat-sidebar-starter"
                      disabled={startersDisabled}
                      className="rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm bg-neutral-200 px-3 py-2 text-left text-sm leading-[1.4] text-twin-chat-fg transition-colors hover:bg-neutral-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 dark:disabled:hover:bg-neutral-700"
                      onClick={() => onSend?.(starter)}
                    >
                      {starter}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Stances / key attributes when available */}
      {participant.stances && Object.keys(participant.stances).length > 0 && (
        <>
          <SidebarDivider />
          <div className="flex flex-col gap-2">
            <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
              Key perspectives
            </Text>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(participant.stances).map(([topic, stance]) => (
                <span
                  key={topic}
                  className="inline-flex items-center rounded-full bg-twin-chat-bubble/40 px-2.5 py-1 text-xs text-twin-chat-muted"
                  title={topic}
                >
                  {stance}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
